import { CalendarDays, Clock, MapPin, Tag } from "lucide-react";
import type { ReactNode } from "react";
import type { CoachAssignment, CoachAssignmentStatus } from "@flexv-coach/shared";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";
import { Field, Textarea } from "@/components/ui/Field";
import { formatDuration, formatLongDate, formatTimeRange, minutesBetween } from "@/lib/date";
import { roleLabel, sourceLabel, statusLabel, statusTone } from "@/lib/labels";

interface AssignmentDetailDrawerProps {
    assignment: CoachAssignment | null;
    onClose: () => void;
    onUpdateStatus: (status: CoachAssignmentStatus, notes?: string) => Promise<unknown>;
    updating: boolean;
}

const NEXT_ACTIONS: Record<CoachAssignmentStatus, CoachAssignmentStatus[]> = {
    scheduled: ["confirmed", "cancelled"],
    confirmed: ["completed", "cancelled"],
    ongoing: ["completed", "cancelled"],
    completed: [],
    cancelled: [],
    no_show: [],
};

const ACTION_VARIANT: Record<CoachAssignmentStatus, "primary" | "danger"> = {
    scheduled: "primary",
    confirmed: "primary",
    ongoing: "primary",
    completed: "primary",
    cancelled: "danger",
    no_show: "danger",
};

function DetailRow({
    icon,
    label,
    value,
}: {
    icon: ReactNode;
    label: string;
    value: string;
}) {
    return (
        <div className="flex items-start gap-3">
            <div className="app-content-subtle app-shell-text-muted rounded-lg p-2">{icon}</div>
            <div className="min-w-0">
                <p className="app-text-soft text-xs uppercase tracking-wide">{label}</p>
                <p className="app-text-primary text-sm font-medium">{value}</p>
            </div>
        </div>
    );
}

export function AssignmentDetailDrawer({
    assignment,
    onClose,
    onUpdateStatus,
    updating,
}: AssignmentDetailDrawerProps) {
    return (
        <Drawer
            open={assignment !== null}
            onClose={onClose}
            title={assignment?.sourceName ?? "Session detail"}
            description={assignment ? statusLabel(assignment.status) : undefined}
            footer={
                assignment && NEXT_ACTIONS[assignment.status].length > 0 ? (
                    <div className="flex gap-2">
                        {NEXT_ACTIONS[assignment.status].map((status) => (
                            <Button
                                key={status}
                                variant={ACTION_VARIANT[status]}
                                fullWidth
                                loading={updating}
                                onClick={() => void onUpdateStatus(status)}
                            >
                                Mark as {statusLabel(status)}
                            </Button>
                        ))}
                    </div>
                ) : undefined
            }
        >
            {assignment && (
                <div className="flex flex-col gap-5">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={statusTone(assignment.status)}>
                            {statusLabel(assignment.status)}
                        </Badge>
                        <Badge tone="neutral">{roleLabel(assignment.role)}</Badge>
                        {assignment.sport && (
                            <Badge tone="primary">{assignment.sport.name}</Badge>
                        )}
                    </div>

                    <div className="flex flex-col gap-4">
                        <DetailRow
                            icon={<CalendarDays className="h-4 w-4" />}
                            label="Date"
                            value={formatLongDate(assignment.assignmentDate)}
                        />
                        <DetailRow
                            icon={<Clock className="h-4 w-4" />}
                            label="Time"
                            value={`${formatTimeRange(assignment.startTime, assignment.endTime)} · ${formatDuration(
                                minutesBetween(assignment.startTime, assignment.endTime),
                            )}`}
                        />
                        <DetailRow
                            icon={<MapPin className="h-4 w-4" />}
                            label="Outlet"
                            value={assignment.outlet?.name ?? "—"}
                        />
                        <DetailRow
                            icon={<Tag className="h-4 w-4" />}
                            label="Source"
                            value={sourceLabel(assignment.sourceType)}
                        />
                    </div>

                    <Field label="Notes">
                        <Textarea
                            defaultValue={assignment.notes ?? ""}
                            placeholder="Optional notes about this session"
                            rows={4}
                        />
                    </Field>
                </div>
            )}
        </Drawer>
    );
}
